import type { IElementImg360 } from "@faro-lotv/ielement-types";
import { lotvCache } from "@faro-lotv/lotv";

/**
 * Reloads all image resources of a given Img360 from the LotVCache
 *
 * @param img360 The Img360 for which to reload the resources.
 */
export function purgeImg360FromLotVCache(img360: IElementImg360): void {
  lotvCache.delete(img360.uri);
  if (img360.signedUri) lotvCache.delete(img360.signedUri);
  if (img360.thumbnailUri) lotvCache.delete(img360.thumbnailUri);
  if (img360.signedThumbnailUri) lotvCache.delete(img360.signedThumbnailUri);
  if (img360.levelsOfDetail) {
    for (const level of img360.levelsOfDetail) {
      for (const uri of level.sources) {
        lotvCache.delete(uri);
      }

      if (level.signedSources) {
        for (const uri of level.signedSources) {
          lotvCache.delete(uri);
        }
      }
    }
  }
}

/**
 * Reloads all image resources of a given Img360 to clear the browser cache.
 *
 * @param img360 The Img360 for which to reload the resources.
 */
export async function reloadImg360InBrowserCache(
  img360: IElementImg360,
): Promise<void> {
  const promises: Array<Promise<unknown>> = [];

  if (img360.signedThumbnailUri) {
    promises.push(fetch(img360.signedThumbnailUri, { cache: "reload" }));
  } else if (img360.thumbnailUri) {
    promises.push(fetch(img360.thumbnailUri, { cache: "reload" }));
  }

  if (img360.levelsOfDetail) {
    for (const level of img360.levelsOfDetail) {
      if (level.signedSources) {
        for (const uri of level.signedSources) {
          promises.push(fetch(uri, { cache: "reload" }));
        }
      } else {
        for (const uri of level.sources) {
          promises.push(fetch(uri, { cache: "reload" }));
        }
      }
    }
  } else if (img360.signedUri) {
    promises.push(fetch(img360.signedUri, { cache: "reload" }));
  } else {
    promises.push(fetch(img360.uri, { cache: "reload" }));
  }

  await Promise.all(promises);
}
